import { ApiClientConfig, RequestOptions, HttpMethod } from './types';
import { HttpError, NetworkError, TimeoutError, ValidationError } from './errors';
import { serializeFormData, buildQueryString } from './utils/serialize';

/**
 * Type-safe REST API client for browser and Node.js environments
 * 
 * @example
 * ```ts
 * const client = new ApiClient({
 *   baseUrl: 'https://api.example.com',
 *   timeout: 5000
 * });
 * 
 * const users = await client.get<User[]>('/users');
 * ```
 */
export class ApiClient {
  private config: ApiClientConfig;

  /**
   * Creates a new API client instance
   * @param config - Client configuration options
   * @throws {TypeError} If baseUrl is invalid or timeout is not a positive number
   */
  constructor(config: ApiClientConfig) {
    // Validate baseUrl
    if (!config.baseUrl || typeof config.baseUrl !== 'string') {
      throw new TypeError('baseUrl is required and must be a string');
    }

    try {
      new URL(config.baseUrl);
    } catch {
      throw new TypeError('baseUrl must be a valid URL');
    }

    // Validate timeout if provided
    if (config.timeout !== undefined) {
      if (typeof config.timeout !== 'number' || config.timeout <= 0) {
        throw new TypeError('timeout must be a positive number');
      }
    }

    // Set defaults
    this.config = {
      timeout: 30000, // 30 seconds default
      headers: {},
      ...config
    };
  }

  /**
   * Create a new client with updated configuration
   * @param config - Partial configuration to merge
   * @returns New ApiClient instance with merged configuration
   * @example
   * ```ts
   * const client1 = new ApiClient({ baseUrl: 'https://api.example.com' });
   * const client2 = client1.withConfig({ timeout: 5000 });
   * // client1 unchanged, client2 has new timeout
   * ```
   */
  withConfig(config: Partial<ApiClientConfig>): ApiClient {
    return new ApiClient({ ...this.config, ...config });
  }

  /**
   * Make a GET request
   * @param url - Request URL (relative to baseUrl)
   * @param options - Optional request configuration
   * @returns Promise resolving to typed response data
   * @example
   * ```ts
   * const users = await client.get<User[]>('/users', {
   *   params: { page: 1, limit: 10 }
   * });
   * ```
   */
  async get<T>(
    url: string, 
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('GET', url, undefined, options);
  }

  /**
   * Make a POST request
   * @param url - Request URL (relative to baseUrl)
   * @param data - Request body data
   * @param options - Optional request configuration
   * @returns Promise resolving to typed response data
   * @example
   * ```ts
   * const user = await client.post<User>('/users', {
   *   name: 'John Doe',
   *   email: 'john@example.com'
   * });
   * ```
   */
  async post<T>(
    url: string, 
    data?: unknown, 
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>('POST', url, data, options);
  }

  /**
   * Core request method with retry logic
   */
  private async request<T>(
    method: HttpMethod,
    url: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    const maxRetries = 3;
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this.executeRequest<T>(method, url, data, options);
      } catch (error) {
        lastError = error as Error;
        
        // Only retry network errors
        if (error instanceof NetworkError && attempt < maxRetries) {
          const delay = Math.pow(2, attempt) * 1000; // Exponential backoff: 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        throw error;
      }
    }

    throw lastError!;
  }

  /**
   * Execute a single request
   */
  private async executeRequest<T>(
    method: HttpMethod,
    url: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    // Build full URL
    const fullUrl = this.buildUrl(url, options?.params);
    
    // Determine content type
    const contentType = options?.contentType ?? 'json';
    
    // Build headers
    const headers = this.buildHeaders(contentType, options?.headers);
    
    // Serialize body based on content type
    const body = this.serializeBody(data, contentType);

    // Setup timeout
    const controller = new AbortController();
    const timeoutMs = options?.timeout ?? this.config.timeout ?? 30000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(fullUrl, {
        method,
        headers,
        body,
        signal: options?.signal ?? controller.signal
      });

      clearTimeout(timeoutId);

      // Handle HTTP errors
      if (!response.ok) {
        const errorData = await this.parseResponse(response);
        throw new HttpError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          response.statusText,
          errorData
        );
      }

      // Parse and return response
      return await this.parseResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);

      // Re-throw known errors
      if (error instanceof HttpError) {
        throw error;
      }

      if (error instanceof ValidationError) {
        throw error;
      }

      // Handle timeout
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new TimeoutError(`Request timeout after ${timeoutMs}ms`);
      }

      // Handle network errors
      throw new NetworkError('Network request failed', error);
    }
  }

  /**
   * Build full URL with query parameters
   */
  private buildUrl(path: string, params?: Record<string, any>): string {
    const base = this.config.baseUrl.replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const fullPath = `${base}${cleanPath}`;
    
    if (params) {
      return fullPath + buildQueryString(params);
    }
    
    return fullPath;
  }

  /**
   * Build request headers
   */
  private buildHeaders(
    contentType: 'json' | 'form',
    customHeaders?: Record<string, string>
  ): Record<string, string> {
    const headers: Record<string, string> = {
      ...this.config.headers,
      ...customHeaders
    };

    // Set Content-Type based on request type
    if (contentType === 'json') {
      headers['Content-Type'] = 'application/json';
    } else if (contentType === 'form') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }

    return headers;
  }

  /**
   * Serialize request body
   */
  private serializeBody(
    data: unknown, 
    contentType: 'json' | 'form'
  ): string | undefined {
    if (!data) return undefined;

    if (contentType === 'form') {
      return serializeFormData(data as Record<string, any>);
    }

    return JSON.stringify(data);
  }

  /**
   * Parse response body
   */
  private async parseResponse(response: Response): Promise<any> {
    const contentType = response.headers.get('content-type');
    
    // Handle empty responses (204 No Content)
    if (response.status === 204 || !contentType) {
      return undefined;
    }

    // Parse JSON
    if (contentType?.includes('application/json')) {
      try {
        return await response.json();
      } catch (error) {
        throw new ValidationError('Invalid JSON response', error);
      }
    }

    // Default to text
    return await response.text();
  }
}

